import { createSelector } from 'reselect'

import { AgeGroupCode, PatientGroup } from '~/clients/derived-api-types'
import { AgeGroup, type ResolvedSurgeryTypeGroup, selectGetResolvedSurgeryTypeGroup } from '~/storm'

import { AgeGroupOption, PatientGroupData } from './types'

/**
 * Returns a record of patient group IDs and their selected age groups.
 *
 * @param selectedGroups - Array of selected patient groups.
 */
function getSelectedAgeGroupsByPatientGroupId(selectedGroups: PatientGroupData[]): Record<string, string[]> {
    return selectedGroups.reduce<Record<string, string[]>>((acc, group) => {
        if (!group.age_group) {
            return acc
        }

        ;(acc[group.patient_group_id] ??= []).push(group.age_group)
        return acc
    }, {})
}

export const selectGetDisabledSurgeryTypes = createSelector(selectGetResolvedSurgeryTypeGroup, getSurgeryTypeGroups => {
    /**
     * Returns an array of patient group IDs that are disabled based on the selected groups.
     *
     * A selected group is disabled if it has no unselected age groups or if all available age groups are already selected.
     *
     * If a surgery type group has no age restrictions, it can only be selected once.
     *
     * @param selectedGroups - An array of selected patient groups.
     * @returns {string[]} - An array of disabled patient group IDs.
     */
    function bySelection(selectedGroups: PatientGroupData[]): string[] {
        const selectedAgeGroupsByPatientGroups = getSelectedAgeGroupsByPatientGroupId(selectedGroups)

        function withoutUnselectedAgeGroupOptions() {
            return (group: PatientGroupData) => {
                const surgeryTypeGroup = getSurgeryTypeGroups.byCode(group.patient_group_id)
                const ageGroupOptions = surgeryTypeGroup?.ageRestrictions.map(ageRestriction => ageRestriction.age_group_code) ?? []

                // Selected group is disabled if there are no available age groups
                if (ageGroupOptions.length === 0) return true

                // The current group is disabled if the selection already contains all available options
                return ageGroupOptions.every(ageGroup => (selectedAgeGroupsByPatientGroups[group.patient_group_id] ?? []).includes(ageGroup))
            }
        }

        return selectedGroups.filter(withoutUnselectedAgeGroupOptions()).map(group => group.patient_group_id)
    }

    return { bySelection }
})

export const selectGetAgeGroupOptions = createSelector(selectGetResolvedSurgeryTypeGroup, getSurgeryTypeGroups => {
    /**
     * Returns an array of age group options for a surgery type group by code.
     *
     * If an age group is already selected for a specific surgery type group, the age group will be marked as disabled for other instances of that surgery type group.
     *
     * In other words: there can be multiple instances of the same surgery type group, but each of its age group options can only be selected once.
     *
     * @param selectedGroups - An array of selected patient groups.
     * @param code - The surgery type group code for which to retrieve age group options.
     * @returns - An array of age group options for the specified surgery type group - each with a label, value, and disabled state.
     */
    function bySelectionAndPatientGroup(selectedGroups: PatientGroup[], code: string): AgeGroupOption[] {
        const resolvedGroup = getSurgeryTypeGroups.byCode(code)

        if (!resolvedGroup?.ageRestrictions.length) {
            return []
        }

        return resolvedGroup.ageRestrictions.map(ageGroup => ({
            label: ageGroup.display_name ?? '',
            value: ageGroup.age_group_code,
            disabled: selectedGroups.some(group => group.patient_group_id === code && group.age_group === ageGroup.age_group_code),
        }))
    }

    return { bySelectionAndPatientGroup }
})

/**
 * Returns the first available age group for a given surgery type group.
 *
 * If there are no age groups for the provided surgery type group, it returns null.
 * If there are available age groups, it returns the first one that is not already selected.
 *
 * @param resolvedGroup - The resolved surgery type group.
 * @param selectedGroups - An array of currently selected patient groups.
 * @returns {AgeGroupCode | null} - The first available age group code or null if none are available.
 */
function findFirstUnselectedAgeGroup(resolvedGroup: ResolvedSurgeryTypeGroup, selectedGroups: PatientGroupData[]): AgeGroupCode | null {
    if (resolvedGroup.ageRestrictions.length === 0) {
        return null
    }

    function isAgeGroupUnselected(ageGroup: AgeGroup): boolean {
        return !selectedGroups.some(group => group.patient_group_id === resolvedGroup.code && group.age_group === ageGroup.age_group_code)
    }

    return resolvedGroup.ageRestrictions.find(isAgeGroupUnselected)?.age_group_code ?? null
}

export const selectGetPatientGroupData = createSelector(selectGetResolvedSurgeryTypeGroup, getSurgeryTypeGroups => {
    /**
     * Returns patient group form data based on the provided surgery type code.
     *
     * If the resolved group has any specified age groups, it defaults to the first age group.
     *
     * @param code - The surgery type code used to identify the patient group.
     * @returns {PatientGroupData | null} - The patient group data if found, otherwise null.
     */
    function byCode(code: string): PatientGroupData | null {
        const resolvedGroup = getSurgeryTypeGroups.byCode(code)

        if (!resolvedGroup) {
            return null
        }

        return {
            patient_group_id: code,
            age_group: resolvedGroup.ageRestrictions[0]?.age_group_code ?? null,
            count: 1,
        }
    }

    /**
     * Returns patient group form data based on the provided surgery type code.
     *
     * If the resolved group has any specified age groups, it selects the first age group that is not already selected.
     *
     * @param code - The surgery type code used to identify the patient group.
     * @param selectedPatientGroups - An array of currently selected patient groups.
     * @returns {PatientGroupData | null} - The patient group data if found, otherwise null.
     */
    function byCodeAndSelection(code: string, selectedPatientGroups: PatientGroupData[]): PatientGroupData | null {
        const resolvedGroup = getSurgeryTypeGroups.byCode(code)

        if (!resolvedGroup) {
            return null
        }

        return {
            patient_group_id: code,
            age_group: findFirstUnselectedAgeGroup(resolvedGroup, selectedPatientGroups),
            count: 1,
        }
    }

    return { byCodeAndSelection, byCode }
})
